import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiGet, apiPatch } from '../../api/client';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';

interface TranscriptionConfig {
  provider: string;
  isActive: boolean;
  maxDurationSec: number;
  languageHint: string;
}

export function SuperOrgTranscriptionPage() {
  const { org, token, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<TranscriptionConfig | null>(null);

  // Form state
  const [provider, setProvider] = useState<'whisper' | 'disabled'>('disabled');
  const [apiKey, setApiKey] = useState('');
  const [maxDuration, setMaxDuration] = useState(120);
  const [languageHint, setLanguageHint] = useState('es');

  const loadConfig = useCallback(async () => {
    try {
      const data = await apiGet<TranscriptionConfig>(
        `/api/super/organizations/${org.id}/transcription-config`,
        token
      );
      setConfig(data);
      setProvider(data.provider as 'whisper' | 'disabled');
      setMaxDuration(data.maxDurationSec);
      setLanguageHint(data.languageHint);
    } catch (e) {
      // If endpoint doesn't exist yet, just use defaults
      console.warn('[transcription] load config failed:', e);
    } finally {
      setLoading(false);
    }
  }, [org.id, token]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = useCallback(async () => {
    setPageErr('');
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        transcriptionProvider: provider,
        transcriptionMaxDurationSec: maxDuration,
        transcriptionLanguageHint: languageHint,
      };
      // Only send API key if user typed something
      if (apiKey.trim()) {
        body.transcriptionApiKey = apiKey.trim();
      }
      await apiPatch(
        `/api/super/organizations/${org.id}/transcription`,
        body,
        token,
        { successMessage: 'Configuración de transcripción actualizada' }
      );
      setApiKey(''); // Clear after save
      await loadConfig();
    } catch (e) {
      setPageErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [org.id, token, provider, apiKey, maxDuration, languageHint, setPageErr, loadConfig]);

  const handleClearKey = useCallback(async () => {
    if (!window.confirm('¿Eliminar la API key de transcripción? Esto desactivará la transcripción.')) return;
    setPageErr('');
    setSaving(true);
    try {
      await apiPatch(
        `/api/super/organizations/${org.id}/transcription`,
        { transcriptionApiKey: '' },
        token,
        { successMessage: 'API key eliminada' }
      );
      await loadConfig();
    } catch (e) {
      setPageErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [org.id, token, setPageErr, loadConfig]);

  if (loading) {
    return <p style={{ color: '#64748b', fontSize: 14 }}>Cargando configuración…</p>;
  }

  return (
    <div>
      <h2 style={{ fontSize: 17, margin: '0 0 8px', color: '#0f172a' }}>Transcripción de audio</h2>
      <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px' }}>
        Configuración del servicio de transcripción (Whisper) para <strong>{org.name}</strong>.
        Aplica a todos los agentes de la organización.
      </p>

      {/* Status indicator */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 8,
          marginBottom: 20,
          fontSize: 13,
          fontWeight: 600,
          background: config?.isActive ? '#ecfdf5' : '#fef2f2',
          color: config?.isActive ? '#065f46' : '#991b1b',
          border: `1px solid ${config?.isActive ? '#a7f3d0' : '#fecaca'}`,
        }}
      >
        <span style={{ fontSize: 16 }}>{config?.isActive ? '🟢' : '🔴'}</span>
        {config?.isActive ? 'Activo — transcribiendo audios' : 'Inactivo — audios no se transcriben'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480 }}>
        {/* Provider */}
        <label style={superOrgField}>
          Proveedor
          <select
            style={superOrgInput}
            value={provider}
            onChange={(e) => setProvider(e.target.value as 'whisper' | 'disabled')}
          >
            <option value="disabled">Deshabilitado</option>
            <option value="whisper">OpenAI Whisper</option>
          </select>
        </label>

        {/* API Key */}
        <label style={superOrgField}>
          API Key (OpenAI)
          <input
            style={superOrgInput}
            type="password"
            placeholder={config?.isActive ? '••••••••••• (configurada)' : 'sk-...'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            {config?.isActive
              ? 'Dejá en blanco para mantener la key actual. '
              : 'Ingresá una API key de OpenAI para activar la transcripción. '}
            Se guarda encriptada.
          </span>
        </label>

        {config?.isActive ? (
          <button
            type="button"
            style={{
              ...superOrgBtn,
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              fontSize: 12,
              padding: '6px 12px',
              alignSelf: 'flex-start',
            }}
            onClick={() => void handleClearKey()}
            disabled={saving}
          >
            Eliminar API key
          </button>
        ) : null}

        {/* Max Duration */}
        <label style={superOrgField}>
          Duración máxima (segundos)
          <input
            style={{ ...superOrgInput, maxWidth: 120 }}
            type="number"
            min={10}
            max={300}
            value={maxDuration}
            onChange={(e) => setMaxDuration(Number(e.target.value))}
          />
          <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            Audios más largos no se transcriben (10-300 seg, default 120).
          </span>
        </label>

        {/* Language Hint */}
        <label style={superOrgField}>
          Idioma preferido
          <select
            style={{ ...superOrgInput, maxWidth: 200 }}
            value={languageHint}
            onChange={(e) => setLanguageHint(e.target.value)}
          >
            <option value="es">Español (es)</option>
            <option value="en">English (en)</option>
            <option value="pt">Português (pt)</option>
            <option value="fr">Français (fr)</option>
            <option value="de">Deutsch (de)</option>
            <option value="it">Italiano (it)</option>
            <option value="auto">Auto-detectar</option>
          </select>
          <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            Hint para mejorar la precisión de Whisper. "Auto" deja que el modelo detecte.
          </span>
        </label>

        {/* Save button */}
        <button
          type="button"
          style={{ ...superOrgBtn, alignSelf: 'flex-start', opacity: saving ? 0.6 : 1 }}
          onClick={() => void handleSave()}
          disabled={saving}
        >
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}
